import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Boxes, Plus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = { vehicule: "Véhicule", machine: "Machine", box: "Box", cabine: "Cabine", salle: "Salle", equipement: "Équipement", materiel: "Matériel" };
const empty = { name: "", type: "equipement", notes: "", active: true };

export default function Resources() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/resources")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => { setEditId(r.id); setForm({ ...empty, ...r }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/resources/${editId}`, form); else await api.post("/resources", form);
      toast.success("Ressource enregistrée"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/resources/${id}`); toast.success("Supprimée"); load(); };

  return (
    <div>
      <PageHeader title="Ressources" subtitle="Réservez une ressource avec une intervention (véhicule, box, cabine, machine…). Conflits détectés automatiquement." icon={Boxes}
        actions={<Button onClick={openNew} data-testid="btn-new-resource" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Nouvelle ressource</Button>} />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        : rows.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">Aucune ressource.</div>
        : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{rows.map((r) => (
          <div key={r.id} data-testid={`resource-row-${r.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3">
            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openEdit(r)}>
              <div className="text-sm font-medium text-slate-800">{r.name}</div>
              <div className="text-xs text-slate-400">{TYPES[r.type] || r.type}{!r.active && " · inactive"}</div>
              {r.notes && <div className="text-xs text-slate-500 mt-1">{r.notes}</div>}
            </div>
            <button onClick={() => del(r.id)} data-testid={`resource-del-${r.id}`} className="p-2 rounded-lg hover:bg-red-500/10 text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}</div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-900">{editId ? "Modifier" : "Nouvelle ressource"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="resource-name" className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger data-testid="resource-type" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-emerald-500" /> Active</label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-resource" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
